import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Heart, Star, Moon, Gem, Eye, Hand, TrendingUp, Scale, Stethoscope, Users, Loader2, AlertCircle } from 'lucide-react';
import { getCategoryById } from '../../../api/categories.api';
import type { Category, SubCategory } from '@/types/category.type';
import { useNavigate } from 'react-router-dom';
import { useCategories } from '../../../hooks/useCategories';

// 🟢 SUBCATEGORY SLUG MAPPER FOR CLEAN DIRECT ROUTING
const SUBCATEGORY_SLUG_MAP: Record<string, string> = {
  'love & relationship astrology': 'love-and-relationship-astrology',
  'love & relationship': 'love-and-relationship-astrology',
  'career & business astrology experts': 'career-and-business-astrology',
  'career & business astrology': 'career-and-business-astrology',
  'vedic astrologers': 'vedic-astrology',
  'tarot card readers': 'tarot-card-readers',
  'lal kitab experts': 'lal-kitab-experts',
  'numerologists': 'numerologists',
  'palmists (hast rekha experts)': 'palmists',
  'face reading experts (samudrik shastra)': 'face-reading-experts',
  'western astrologers': 'western-astrologers',
  'nadi astrologers': 'nadi-astrologers',
  'health astrology': 'health-astrology',
  'remedies & vastu consultants': 'remedies-and-vastu-consultants',
  'family and marriage law': 'family-law',
  'family law': 'family-law',
  'criminal law': 'criminal-defense',
  'criminal defense lawyers': 'criminal-defense',
  'real estate law': 'real-estate-law',
  'real estate & property lawyers': 'real-estate-law',
  'consumer protection law': 'consumer-protection-law',
  'business law': 'business-law',
  'general physician': 'general-physician',
  'dermatology': 'dermatology',
  'gynecology': 'gynecology',
};

const getSubcategorySlug = (name: string): string => {
  const lower = name.toLowerCase().trim();
  if (SUBCATEGORY_SLUG_MAP[lower]) {
    return SUBCATEGORY_SLUG_MAP[lower];
  }
  return lower
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
};

// Icon mapping for subcategories
const iconMap: Record<string, any> = {
  Star, Sparkles, Heart, Moon, Gem, Eye, Hand, TrendingUp, 
  Scale, Stethoscope, Users
};

// Default icon if none specified
const getIcon = (iconName?: string) => {
  if (!iconName) return Sparkles;
  return iconMap[iconName] || Sparkles;
};

// Generate gradient based on index
const gradients = [
  'from-purple-100 to-pink-100',
  'from-blue-100 to-indigo-100',
  'from-green-100 to-emerald-100',
  'from-pink-100 to-rose-100',
  'from-yellow-100 to-orange-100',
  'from-cyan-100 to-blue-100',
  'from-indigo-100 to-purple-100',
  'from-teal-100 to-cyan-100',
  'from-red-100 to-orange-100',
];

const getGradient = (index: number) => {
  return gradients[index % gradients.length];
};

interface CategoryPreviewProps {
  categoryId: string;
  onClose: () => void;
}

export function CategoryPreview({ categoryId, onClose }: CategoryPreviewProps) {
  const [category, setCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategory = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getCategoryById(Number(categoryId));
        
        // Normalize response
        const data = res.data?.data || res.data;
        setCategory(data);
      } catch (err) {
        console.error('❌ Category fetch error:', err);
        setError('Failed to load category details');
      } finally {
        setLoading(false);
      }
    };

    if (categoryId) {
      fetchCategory();
    }
  }, [categoryId]);

  const subcategories = category?.subCategories || [];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
            <div>
              <h2 className="text-gray-900 mb-1">All Specializations</h2>
              <p className="text-sm text-gray-600">Choose a specialization to find experts</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              // Loading State
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : error ? (
              // Error State
              <div className="flex flex-col items-center justify-center py-20">
                <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
                <p className="text-gray-600 text-center mb-2">{error}</p>
                <button
                  onClick={onClose}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  Close
                </button>
              </div>
            ) : subcategories.length === 0 ? (
              // No Subcategories
              <div className="flex flex-col items-center justify-center py-20">
                <Eye className="w-12 h-12 text-gray-400 mb-4" />
                <p className="text-gray-600 text-center mb-2">No specializations available yet</p>
                <p className="text-sm text-gray-500 text-center">Check back soon for updates!</p>
              </div>
            ) : (
              // Subcategories Grid
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {subcategories.map((subcategory: SubCategory, index: number) => (
                  <SubcategoryCard 
                    key={subcategory.id} 
                    subcategory={subcategory} 
                    index={index} 
                    categoryName={category?.name || ''}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-6 bg-gray-50">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
              <p className="text-sm text-gray-600">
                Can't find what you're looking for?
              </p>
              <div className="flex gap-3">
                <button className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                  View All Categories
                </button>
                <a 
                  href="https://wa.me/918368832850?text=Hi%20True%20Adviso%20team%2C%20I%20am%20visiting%20your%20website%20and%20need%20some%20guidance."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  Talk to Team
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function SubcategoryCard({ subcategory, index, categoryName }: { subcategory: SubCategory; index: number; categoryName: string }) {
  const [isHovered, setIsHovered] = React.useState(false);
  const navigate = useNavigate();
  
  // Determine badge based on backend flags
  const badge = subcategory.isPopular 
    ? 'Popular' 
    : subcategory.isTrending 
    ? 'Trending' 
    : undefined;

  // Get gradient and icon
  const gradient = getGradient(index);
  const Icon = getIcon('Sparkles');

  // 🟢 DIRECT CLEAN SUBCATEGORY ROUTING LOGIC
  const handleViewExperts = () => {
    if (!categoryName || !subcategory.name) return;
    
    const formattedCat = categoryName.toLowerCase().trim();
    const slug = getSubcategorySlug(subcategory.name);
    
    // Direct navigation to /experts/:category/:subCategorySlug
    navigate(`/experts/${formattedCat}/${slug}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="cursor-pointer"
      onClick={handleViewExperts}
    >
      <motion.div
        whileHover={{ y: -4 }}
        transition={{ type: 'spring', stiffness: 300 }}
        className={`relative bg-gradient-to-br ${gradient} rounded-xl p-5 h-full border-2 border-transparent transition-all duration-300 ${
          isHovered ? 'shadow-lg border-blue-400' : 'shadow-sm'
        }`}
      >
        {/* Badge */}
        {badge && (
          <div className="absolute top-3 right-3">
            <span className="inline-block px-2 py-1 bg-blue-600 text-white rounded-full text-xs">
              {badge}
            </span>
          </div>
        )}

        {/* Icon */}
        <div className="mb-3">
          <div className={`w-12 h-12 rounded-lg bg-white shadow-sm flex items-center justify-center transition-transform duration-300 ${
            isHovered ? 'scale-110' : ''
          }`}>
            <Icon className="w-6 h-6 text-blue-600" />
          </div>
        </div>

        {/* Content */}
        <h4 className="text-gray-900 mb-2">{subcategory.name}</h4>
        <p className="text-xs text-gray-600 mb-3 line-clamp-2">
          {subcategory.description || 'Expert guidance and consultation'}
        </p>
        
        {/* Expert Count */}
        <div className="text-xs text-gray-700 mb-3">
          <span className="font-medium">25+</span> experts
        </div>

        {/* View Experts Button */}
        <motion.button
          onClick={(e) => {
            e.stopPropagation();
            handleViewExperts();
          }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : 10 }}
          transition={{ duration: 0.2 }}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs"
        >
          View Experts
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

/* =======================
   UI Category Shape
   ======================= */
interface UICategory {
  id: string;
  name: string;
  description: string;
  expertCount: number;
  icon?: string | null;
  gradient?: string | null;
  iconColor?: string | null;
}

interface CategoryGridProps {
  searchQuery: string;
  filters?: any;
  onSelectCategory: (categoryId: string) => void;
}

function extractGradient(bg: string) {
  const fromMatch = bg.match(/from-\[#([0-9A-Fa-f]{6})\]\/(\d+)/);
  const toMatch = bg.match(/to-\[#([0-9A-Fa-f]{6})\]\/(\d+)/);

  if (!fromMatch || !toMatch) return '';

  const fromColor = `rgba(${hexToRgb(fromMatch[1])}, ${Number(fromMatch[2]) / 100})`;
  const toColor = `rgba(${hexToRgb(toMatch[1])}, ${Number(toMatch[2]) / 100})`;

  return `${fromColor}, ${toColor}`;
}

function hexToRgb(hex: string) {
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r}, ${g}, ${b}`;
}

/* =======================
   SKELETON CARD
   ======================= */
function SkeletonCard({ index }: { index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="relative rounded-2xl p-6 h-full border-2 border-red-200 bg-gradient-to-br from-red-50 to-red-100 w-full sm:w-[280px]"
    >
      <div className="mb-4">
        <div className="w-14 h-14 rounded-xl bg-red-100 shadow-sm flex items-center justify-center">
          <AlertCircle className="w-7 h-7 text-red-400" />
        </div>
      </div>

      <h3 className="text-gray-900 mb-2 font-semibold">Unable to Load</h3>
      <p className="text-sm text-gray-600 mb-4">
        We're having trouble loading popular categories right now.
      </p>

      <div className="text-xs text-gray-500 mb-2">
        <span className="font-medium">🧙 Mr. Developer is fighting a nasty bug with his stick!</span>
      </div>
      <div className="text-xs text-gray-400">
        Stay tuned, Coming soon.
      </div>
    </motion.div>
  );
}

export function CategoryGrid({ searchQuery, onSelectCategory }: CategoryGridProps) {
  const { categories, loading, error } = useCategories();

  const uiCategories: UICategory[] = categories.map((cat) => ({
    id: String(cat.id),
    name: cat.name,
    description: cat.description || '',
    expertCount: 100,
    icon: cat.icon ?? null,
    gradient: cat.bg ?? '',
    iconColor: cat.iconColor ?? '#2563eb',
  }));

  const filteredCategories = uiCategories.filter(
    (category) =>
      category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      category.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const showSkeletons = error || (!loading && categories.length === 0);

  if (loading) return null;

  return (
    <div className="mt-12 font-['Poppins'] w-full max-w-7xl mx-auto px-4">
      <h2 className="flex items-center gap-2 text-lg sm:text-xl font-normal text-gray-900 mb-6 sm:mb-8">
        <span className="text-base sm:text-xl">All Categories</span>
        <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
      </h2>
      
      <div className="flex flex-wrap justify-center gap-6 w-full mx-auto">
        {showSkeletons ? (
          Array.from({ length: 3 }).map((_, index) => (
            <SkeletonCard key={index} index={index} />
          ))
        ) : (
          filteredCategories.map((category, index) => (
            <CategoryCard
              key={category.id}
              category={category}
              index={index}
              onSelect={onSelectCategory}
            />
          ))
        )}
      </div>
    </div>
  );
}

function CategoryCard({
  category,
  index,
  onSelect,
}: {
  category: UICategory;
  index: number;
  onSelect: (id: string) => void;
}) {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group cursor-pointer w-full sm:w-[280px]"
      onClick={() => onSelect(category.id)}
    >
      <motion.div
        whileHover={{ y: -8 }}
        transition={{ type: 'spring', stiffness: 300 }}
        style={{
          background: category.gradient
            ? `linear-gradient(135deg, ${extractGradient(category.gradient)})`
            : undefined,
        }}
        className={`relative rounded-2xl p-6 h-full border-2 border-transparent transition-all duration-300 ${
          isHovered ? 'shadow-xl border-blue-400' : 'shadow-sm'
        }`}
      >
        <div className="mb-4">
          <div
            className={`w-14 h-14 rounded-xl bg-white shadow-sm flex items-center justify-center transition-transform duration-300 ${
              isHovered ? 'scale-110' : ''
            }`}
          >
            {category.icon ? (
              <img
                src={category.icon}
                alt={category.name}
                className="w-7 h-7 object-contain"
              />
            ) : null}
          </div>
        </div>

        <h3 className="text-gray-900 mb-2">{category.name}</h3>
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {category.description}
        </p>

        <div className="text-sm text-gray-700 mb-3">
          <span className="font-medium">{category.expertCount}+</span> experts
        </div>

        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : 10 }}
          transition={{ duration: 0.2 }}
          className="w-full px-4 py-2 bg-brand-blue-900 text-white rounded-lg hover:bg-brand-blue-800 transition-colors text-sm"
        >
          Browse this category
        </motion.button>
      </motion.div>
    </motion.div>
  );
}